// Host header — Stream 3 PR-2.
//
// Compact host row at the top of the left content column. Renders
// the host's avatar (or initial), a one-line "Hosted by X" headline,
// a secondary line with their role/since-year, and a row of
// verified-credential badges.
//
// Badges read currentListing.author.attributes.profile.publicData.
// credentials (the Stream 2 data shape) — only admin-reviewed
// credentials that reached 'verified' status are shown.

import React from 'react';
import classNames from 'classnames';

import { AvatarMedium } from '../../../components';
import { ensureUser } from '../../../util/data';

import css from './SectionHostHeader.module.css';

// Mirror the credential keys Stream 2 set up — these live on profile
// publicData and are flipped to 'verified' by admin review.
const STREAM_2_CREDENTIAL_LABELS = {
  foodServicePermit: 'Licensed caterer',
  certificateOfInsurance: 'Insured',
};

const Badge = ({ children }) => (
  <span className={css.verifiedBadge}>✓ {children}</span>
);

const SectionHostHeader = props => {
  const { className, listing, authorDisplayName } = props;
  const author = ensureUser(listing?.author);
  const profile = author?.attributes?.profile || {};
  const publicData = profile?.publicData || {};
  const credentials = publicData?.credentials || {};

  // The role-and-since line. Hosts who've filled in
  // publicData.tagline (Stream 2 design hook) get that text;
  // otherwise we fall back to "Host on Merrily".
  const tagline = publicData?.tagline || 'Host on Merrily';
  const joinYear = author?.attributes?.createdAt
    ? new Date(author.attributes.createdAt).getFullYear()
    : null;
  const sinceText = joinYear ? `Hosting since ${joinYear}` : null;

  // Render badges only for verified credentials. Pending / rejected
  // / expired stay hidden — renters care that the host is currently
  // covered, not that they once started a process.
  const verifiedBadges = Object.keys(STREAM_2_CREDENTIAL_LABELS).filter(
    key => credentials?.[key]?.status === 'verified'
  );

  return (
    <section className={classNames(css.root, className)}>
      <div className={css.row}>
        {/* Real avatar image (Avatar falls back to the host's initials
            when no photo is set). */}
        <AvatarMedium user={author} className={css.avatar} />
        <div className={css.lines}>
          <div className={css.line1}>
            Hosted by {authorDisplayName || 'your host'}
          </div>
          <div className={css.line2}>
            {tagline}
            {sinceText ? ` · ${sinceText}` : ''}
          </div>
        </div>
      </div>
      {verifiedBadges.length > 0 && (
        <div className={css.badges}>
          {verifiedBadges.map(key => (
            <Badge key={key}>{STREAM_2_CREDENTIAL_LABELS[key]}</Badge>
          ))}
        </div>
      )}
    </section>
  );
};

export default SectionHostHeader;
